import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { donemHesapla, oncekiDonemHesapla, satisDonemiMi, type Donem, type SatisDonemi } from '../satis-analiz/donem';

/**
 * SA'nın `useSatisAnalizFiltre`'sinin kanalsız/kategorili eşi. URL anahtarları SA ile AYNI
 * (`donem`/`bas`/`bit`/`sube`), artı `kategori`. `kanal` bu ekranda okunmaz, `kategori` SA'da
 * okunmaz — iki ekran arasında geçerken dönem/şube korunur, ikisi birbirinin parametresini yok
 * sayar (çakışma yok). `donem.ts`'nin hesapları import edilir, kopyalanmaz.
 */
export interface StokFiltre {
  donemTuru: SatisDonemi;
  ozelAralik: { bas: string; bit: string } | null;
  /** null = tum subeler. */
  sube: string | null;
  /** null = tum kategoriler. Ekrandaki '(kategorisiz)' etiketi de burada normal bir deger. */
  kategori: string | null;
}

const VARSAYILAN_DONEM: SatisDonemi = 'aylik';

function gunMu(x: string | null): x is string {
  return x !== null && /^\d{4}-\d{2}-\d{2}$/.test(x) && dayjs(x).format('YYYY-MM-DD') === x;
}

/** URL -> filtre. Gecersiz her deger varsayilana duser (SA ile ayni desen). */
export function filtreOku(sp: URLSearchParams): StokFiltre {
  const d = sp.get('donem');
  const donemTuru: SatisDonemi = satisDonemiMi(d) ? d : VARSAYILAN_DONEM;
  const bas = sp.get('bas');
  const bit = sp.get('bit');
  const ozelAralik =
    donemTuru === 'ozel' && gunMu(bas) && gunMu(bit) && bas <= bit ? { bas, bit } : null;
  const sube = (sp.get('sube') ?? '').trim();
  const kategori = (sp.get('kategori') ?? '').trim();
  return {
    donemTuru,
    ozelAralik,
    sube: sube === '' ? null : sube,
    kategori: kategori === '' ? null : kategori,
  };
}

/** Filtre degisikligi -> yeni URL. Dokunulmayan alanlar korunur; null = anahtar silinir. */
export function filtreYaz(onceki: URLSearchParams, d: Partial<StokFiltre>): URLSearchParams {
  const sp = new URLSearchParams(onceki);
  if (d.donemTuru !== undefined) {
    sp.set('donem', d.donemTuru);
    if (d.donemTuru !== 'ozel') {
      sp.delete('bas');
      sp.delete('bit');
    }
  }
  if (d.ozelAralik !== undefined) {
    if (d.ozelAralik) {
      sp.set('donem', 'ozel');
      sp.set('bas', d.ozelAralik.bas);
      sp.set('bit', d.ozelAralik.bit);
    } else {
      sp.delete('bas');
      sp.delete('bit');
    }
  }
  if (d.sube !== undefined) {
    if (d.sube) sp.set('sube', d.sube);
    else sp.delete('sube');
  }
  if (d.kategori !== undefined) {
    if (d.kategori) sp.set('kategori', d.kategori);
    else sp.delete('kategori');
  }
  return sp;
}

export interface StokFiltresi extends StokFiltre {
  donem: Donem;
  oncekiDonem: { bas: string; bit: string };
  degistir: (d: Partial<StokFiltre>) => void;
}

/** Stok Analiz filtresinin TEK durumu URL'dir: yenileme ve paylasilan baglanti ayni gorunumu acar. */
export function useStokFiltre(): StokFiltresi {
  const [sp, setSp] = useSearchParams();
  const anahtar = sp.toString();
  const filtre = useMemo(() => filtreOku(new URLSearchParams(anahtar)), [anahtar]);

  const hesapTuru: SatisDonemi =
    filtre.donemTuru === 'ozel' && !filtre.ozelAralik ? VARSAYILAN_DONEM : filtre.donemTuru;

  const donem = useMemo<Donem>(() => {
    if (filtre.ozelAralik) {
      const { bas, bit } = filtre.ozelAralik;
      return { bas, bit, tekGun: bas === bit };
    }
    return donemHesapla(hesapTuru);
  }, [filtre.ozelAralik, hesapTuru]);

  const oncekiDonem = useMemo(
    () => oncekiDonemHesapla(donem, hesapTuru),
    [donem, hesapTuru],
  );

  const degistir = useCallback(
    (d: Partial<StokFiltre>) => setSp((onceki) => filtreYaz(onceki, d), { replace: true }),
    [setSp],
  );

  return { ...filtre, donem, oncekiDonem, degistir };
}
